'use client';

import React, { useState, useEffect } from 'react';
import {
  computeStats,
  computePhaseProgress,
  getCurrentWeek,
  getStreak,
  getWeekProgress,
} from '@/lib/storage';
import { getWeekData } from '@/lib/curriculum';
import ProgressRing from './ProgressRing';
import PhaseProgressBars from './PhaseProgressBars';
import StatsRow from './StatsRow';
import CurrentWeekCard from './CurrentWeekCard';
import QuickActions from './QuickActions';
import RecentActivity from './RecentActivity';
import StreakCard from './StreakCard';

export default function DashboardClient() {
  const [mounted, setMounted] = useState(false);
  const [stats, setStats] = useState({
    tasksComplete: 0,
    assessmentsPassed: 0,
    weeksComplete: 0,
    weeksInProgress: 0,
    studyHours: 0,
    overallPct: 0,
    totalTasksAvailable: 400,
  });
  const [phaseProgress, setPhaseProgress] = useState<Record<number, { complete: number; total: number; pct: number }>>({});
  const [currentWeek, setCurrentWeek] = useState(1);
  const [streak, setStreak] = useState({ currentStreak: 0, longestStreak: 0, lastActiveDate: null as string | null });

  const refresh = () => {
    setStats(computeStats());
    setPhaseProgress(computePhaseProgress());
    setCurrentWeek(getCurrentWeek());
    setStreak(getStreak());
  };

  useEffect(() => {
    setMounted(true);
    refresh();
  }, []);

  const weekData = getWeekData(currentWeek);
  const weekProgress = mounted ? getWeekProgress(currentWeek) : null;

  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon...5=Fri, 6=Sat
  const currentDayIndex = dayOfWeek >= 1 && dayOfWeek <= 5 ? dayOfWeek - 1 : 0;

  if (!mounted) {
    return (
      <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {[1, 2, 3].map(i => (
            <div
              key={`skel-row-${i}`}
              style={{
                height: i === 1 ? '200px' : '120px',
                borderRadius: '12px',
                backgroundColor: 'var(--bg-card)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '32px 24px',
        maxWidth: '900px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '28px',
              fontWeight: 400,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            Your Learning Dashboard
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '12px',
            fontFamily: 'IBM Plex Mono, monospace',
            color: 'var(--text-muted)',
          }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--accent)', display: 'inline-block' }} />
          All data stored locally
        </div>
      </div>

      {/* Top bento: ring + streak + current week */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
        }}
      >
        <ProgressRing pct={stats.overallPct} weeksComplete={stats.weeksComplete} />
        <StreakCard streak={streak} />
        <CurrentWeekCard
          weekData={weekData}
          weekProgress={weekProgress}
          currentDayIndex={currentDayIndex}
          onProgressChange={refresh}
        />
      </div>

      {/* Stats row */}
      <StatsRow
        tasksComplete={stats.tasksComplete}
        totalTasks={stats.totalTasksAvailable}
        assessmentsPassed={stats.assessmentsPassed}
        studyHours={stats.studyHours}
        weeksInProgress={stats.weeksInProgress}
      />

      {/* Phase progress */}
      <PhaseProgressBars phaseProgress={phaseProgress} />

      {/* Bottom: quick actions + recent activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <QuickActions currentWeek={currentWeek} />
        <RecentActivity />
      </div>
    </div>
  );
}